import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import * as pdfjsLib from 'pdfjs-dist';
import { ExtractedData, ExtractedField, PdfExtractionResult } from '@app/_models/extracted-data.model';
import { PatternRecognitionConfig, DEFAULT_PATTERN_CONFIG } from '@app/_models/field-mapping.model';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

@Injectable({
  providedIn: 'root'
})
export class PdfExtractionService {
  private patternConfig: PatternRecognitionConfig = DEFAULT_PATTERN_CONFIG;

  constructor() { }

  /**
   * Extract text and data from PDF file
   */
  extractDataFromPdf(file: File): Observable<PdfExtractionResult> {
    const startTime = Date.now();
    
    return from(this.processPdf(file, startTime)).pipe(
      map(result => result),
      catchError(error => {
        console.error('PDF extraction error:', error);
        return [{ 
          success: false, 
          errors: [error.message || 'Failed to extract data from PDF'],
          warnings: []
        }];
      })
    );
  }

  /**
   * Main PDF processing function
   */
  private async processPdf(file: File, startTime: number): Promise<PdfExtractionResult> {
    try {
      // Validate file
      if (!this.validatePdfFile(file)) {
        return {
          success: false,
          errors: ['Invalid PDF file'],
          warnings: []
        };
      }

      // Convert file to array buffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Load PDF document
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      // Extract text from all pages
      const extractedText = await this.extractTextFromPages(pdf);
      
      if (!extractedText.trim()) {
        return {
          success: false,
          errors: ['No text content found in PDF'],
          warnings: ['This might be a scanned document. OCR support coming soon.']
        };
      }

      // Process extracted text using pattern recognition
      const extractedData = this.processExtractedText(extractedText, file, pdf.numPages, startTime);
      
      return {
        success: true,
        extractedData,
        rawText: extractedText,
        errors: [],
        warnings: extractedData.extractionMetadata.extractedFieldsCount === 0 
          ? ['No recognizable data patterns found in the document'] 
          : []
      };

    } catch (error) {
      console.error('PDF processing error:', error);
      return {
        success: false,
        errors: [error.message || 'Failed to process PDF'],
        warnings: []
      };
    }
  }

  /**
   * Validate PDF file
   */
  private validatePdfFile(file: File): boolean {
    // Check file type
    if (file.type !== 'application/pdf') {
      return false;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return false;
    }

    return true;
  }

  /**
   * Extract text from all PDF pages
   */
  private async extractTextFromPages(pdf: any): Promise<string> {
    let fullText = '';
    
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      
      fullText += pageText + '\n';
    }
    
    return fullText;
  }

  /**
   * Process extracted text using pattern recognition
   */
  private processExtractedText(text: string, file: File, pageCount: number, startTime: number): ExtractedData {
    const extractedData: ExtractedData = {
      extractionMetadata: {
        fileName: file.name,
        fileSize: file.size,
        pageCount: pageCount,
        extractionTime: new Date(),
        totalConfidence: 0,
        extractedFieldsCount: 0,
        processingTimeMs: Date.now() - startTime
      }
    };

    let totalConfidence = 0;
    let fieldCount = 0;

    // Extract vehicle registration number
    const registrationMatch = this.extractWithPattern(
      text, 
      this.patternConfig.vehicleRegistration.patterns,
      this.patternConfig.vehicleRegistration.keywords,
      'registrationNumber'
    );
    if (registrationMatch) {
      extractedData.registrationNumber = registrationMatch;
      totalConfidence += registrationMatch.confidence;
      fieldCount++;
    }

    // Extract phone number
    const phoneMatch = this.extractWithPattern(
      text,
      this.patternConfig.phoneNumber.patterns,
      this.patternConfig.phoneNumber.keywords,
      'contactNumber'
    );
    if (phoneMatch) {
      extractedData.contactNumber = phoneMatch;
      totalConfidence += phoneMatch.confidence;
      fieldCount++;
    }

    // Extract email
    const emailMatch = this.extractWithPattern(
      text,
      this.patternConfig.email.patterns,
      this.patternConfig.email.keywords,
      'email'
    );
    if (emailMatch) {
      extractedData.email = emailMatch;
      totalConfidence += emailMatch.confidence;
      fieldCount++;
    }

    // Extract dates
    const dateMatches = this.extractDates(text);
    if (dateMatches.length > 0) {
      // Assign dates based on context
      dateMatches.forEach(dateMatch => {
        if (dateMatch.value.toLowerCase().includes('birth') || dateMatch.value.toLowerCase().includes('dob')) {
          extractedData.dob = dateMatch;
        } else if (dateMatch.value.toLowerCase().includes('issue')) {
          extractedData.issueDate = dateMatch;
        } else if (dateMatch.value.toLowerCase().includes('start')) {
          extractedData.startDate = dateMatch;
        } else if (dateMatch.value.toLowerCase().includes('end') || dateMatch.value.toLowerCase().includes('expiry')) {
          extractedData.endDate = dateMatch;
        } else if (!extractedData.issueDate) {
          extractedData.issueDate = dateMatch; // Default to issue date
        }
        totalConfidence += dateMatch.confidence;
        fieldCount++;
      });
    }

    // Extract vehicle make
    const vehicleMakeMatch = this.extractVehicleMake(text);
    if (vehicleMakeMatch) {
      extractedData.vehicleMake = vehicleMakeMatch;
      totalConfidence += vehicleMakeMatch.confidence;
      fieldCount++;
    }

    // Extract names
    const nameMatches = this.extractNames(text);
    if (nameMatches.length > 0) {
      if (nameMatches[0]) {
        extractedData.firstName = nameMatches[0];
        totalConfidence += nameMatches[0].confidence;
        fieldCount++;
      }
      if (nameMatches[1]) {
        extractedData.lastName = nameMatches[1];
        totalConfidence += nameMatches[1].confidence;
        fieldCount++;
      }
    }

    // Extract amounts
    const amountMatches = this.extractAmounts(text);
    if (amountMatches.length > 0) {
      extractedData.planTotalAmount = amountMatches[0];
      totalConfidence += amountMatches[0].confidence;
      fieldCount++;
    }

    // Update metadata
    extractedData.extractionMetadata.totalConfidence = fieldCount > 0 ? totalConfidence / fieldCount : 0;
    extractedData.extractionMetadata.extractedFieldsCount = fieldCount;
    extractedData.extractionMetadata.processingTimeMs = Date.now() - startTime;

    return extractedData;
  }

  /**
   * Extract data using pattern matching
   */
  private extractWithPattern(
    text: string, 
    patterns: RegExp[], 
    keywords: string[], 
    fieldName: string
  ): ExtractedField | null {
    for (const pattern of patterns) {
      const matches = text.match(pattern);
      if (matches && matches.length > 0) {
        const value = matches[0].trim();
        const confidence = this.calculateConfidence(text, value, keywords);
        
        return {
          fieldName,
          value,
          confidence,
          source: 'pattern'
        };
      }
    }
    return null;
  }

  /**
   * Extract dates from text
   */
  private extractDates(text: string): ExtractedField[] {
    const dates: ExtractedField[] = [];
    const datePattern = /\b\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}\b/g;
    
    let match;
    while ((match = datePattern.exec(text)) !== null) {
      const value = match[0];
      const context = text.substring(Math.max(0, match.index - 20), match.index + value.length + 20);
      const confidence = this.calculateDateConfidence(context);
      
      dates.push({
        fieldName: 'date',
        value,
        confidence,
        source: 'pattern',
        position: {
          page: 1, // TODO: Calculate actual page
          x: 0,
          y: 0,
          width: 0,
          height: 0
        }
      });
    }
    
    return dates;
  }

  /**
   * Extract vehicle make
   */
  private extractVehicleMake(text: string): ExtractedField | null {
    const knownMakes = this.patternConfig.vehicleMake.knownValues;
    
    for (const make of knownMakes) {
      const pattern = new RegExp(`\\b${make}\\b`, 'gi');
      const match = text.match(pattern);
      
      if (match) {
        return {
          fieldName: 'vehicleMake',
          value: make,
          confidence: 85,
          source: 'pattern'
        };
      }
    }
    
    return null;
  }

  /**
   * Extract names from text
   */
  private extractNames(text: string): ExtractedField[] {
    const names: ExtractedField[] = [];
    const namePattern = /\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\b/g;
    
    const matches = text.match(namePattern);
    if (matches) {
      // Filter out common non-name words
      const filteredNames = matches.filter(name => 
        !this.isCommonWord(name) && name.length > 2
      );
      
      if (filteredNames.length > 0) {
        const firstNameParts = filteredNames[0].split(' ');
        
        names.push({
          fieldName: 'firstName',
          value: firstNameParts[0],
          confidence: 70,
          source: 'pattern'
        });
        
        if (firstNameParts.length > 1) {
          names.push({
            fieldName: 'lastName',
            value: firstNameParts.slice(1).join(' '),
            confidence: 70,
            source: 'pattern'
          });
        }
      }
    }
    
    return names;
  }

  /**
   * Extract amounts from text
   */
  private extractAmounts(text: string): ExtractedField[] {
    const amounts: ExtractedField[] = [];
    const amountPattern = /(?:Rs\.?|INR|₹)?\s*(\d+(?:,\d{3})*(?:\.\d{2})?)/gi;
    
    let match;
    while ((match = amountPattern.exec(text)) !== null) {
      const value = match[1].replace(/,/g, ''); // Remove commas
      const numericValue = parseFloat(value);
      
      if (numericValue > 100 && numericValue < 1000000) { // Reasonable range for insurance
        amounts.push({
          fieldName: 'amount',
          value: value,
          confidence: 60,
          source: 'pattern'
        });
      }
    }
    
    return amounts.slice(0, 3); // Return top 3 amounts
  }

  /**
   * Calculate confidence score for extracted data
   */
  private calculateConfidence(text: string, value: string, keywords: string[]): number {
    let confidence = 50; // Base confidence
    
    // Increase confidence if keywords are found nearby
    const surroundingText = this.getSurroundingText(text, value, 50);
    
    for (const keyword of keywords) {
      if (surroundingText.toLowerCase().includes(keyword.toLowerCase())) {
        confidence += 20;
      }
    }
    
    // Cap confidence at 95
    return Math.min(confidence, 95);
  }

  /**
   * Calculate confidence for date extraction
   */
  private calculateDateConfidence(context: string): number {
    const dateKeywords = ['date', 'dob', 'birth', 'issue', 'start', 'end', 'expiry'];
    let confidence = 60;
    
    for (const keyword of dateKeywords) {
      if (context.toLowerCase().includes(keyword)) {
        confidence += 15;
      }
    }
    
    return Math.min(confidence, 90);
  }

  /**
   * Get surrounding text for context analysis
   */
  private getSurroundingText(text: string, value: string, radius: number): string {
    const index = text.indexOf(value);
    if (index === -1) return '';
    
    const start = Math.max(0, index - radius);
    const end = Math.min(text.length, index + value.length + radius);
    
    return text.substring(start, end);
  }

  /**
   * Check if a word is a common non-name word
   */
  private isCommonWord(word: string): boolean {
    const commonWords = [
      'The', 'And', 'For', 'Are', 'But', 'Not', 'You', 'All', 'Can', 'Had', 'Her', 'Was', 'One', 'Our', 'Out', 'Day', 'Get', 'Use', 'Man', 'New', 'Now', 'Way', 'May', 'Say',
      'Certificate', 'Policy', 'Insurance', 'Vehicle', 'Customer', 'Date', 'Number', 'Amount', 'Details', 'Information'
    ];
    
    return commonWords.includes(word);
  }
}